import moment from "moment";
import { RowData } from "../../common/GridWrapper/DataTable";
import AppConstants from "../../constants";
import {
  enumerateDaysBetweenDates,
  fixDecimals,
  getUniqueValues,
  getValueFromArray,
  localTimeString,
  transformIntoObj,
  transformIntoObjArrays,
  isUndefined,
  transformForScalable,
  displayUnitGrams,
  decodeHtmlEntities,
} from "../../utils/helpers.utils";
import { ADDRESS_STATUS } from "../../constants/CommonConstants";
import DeliveryConstants from "../../constants/SharedConstants/DeliveryConstants";
import {
  MetaData,
  Product,
  Name,
  Address,
  PosInformation,
  CustomerContactInfo,
  Route as RouteCommon,
  Driver as DriverCommon,
  PickUpInformation,
  Reason,
  PaymentDetail,
  Vehicle,
  Statuses,
  ProductsDataTransform,
} from "../SharedTransform/interface";
import { getFilterAttributeValue, getAddressInfo, getTimeSlotPayload } from "../SharedTransform/response.transforms";

export interface Route extends RouteCommon {
  assignee: Driver;
  helper: Driver;
}
export interface DeliverySlot {
  startTime: Date;
  endTime: Date;
  rescheduled?: boolean;
}

export const ROUTE_TYPE = {
  NORMAL_ROUTE: "NORMAL_ROUTE",
  PARENT_ROUTE: "PARENT_ROUTE",
  SUB_ROUTE: "SUB_ROUTE",
  ROUTE_AREA: "ROUTE_AREA",
};

export interface Driver extends DriverCommon {
  id: string;
  name: string;
  vendor: string;
}

export interface DeliveryInformation {
  deliveryInstructions: string;
  deliveryCost: string;
  carrier: string;
  carrierTrackingId: string;
  shipmentSize: string;
  totalCratesCount: number;
  deliverySlot: DeliverySlot;
  customerContactInfo: CustomerContactInfo;
  driver: Driver;
  helper: Driver;
  vehicle: Vehicle;
  transitTime: string;
  actualTransitTime: string;
  dynamicTransitTime: string;
  estimatedTransitTime: string;
  reason: Reason;
  deliveryExecutionStatus?: string;
}

export interface Order {
  id: string;
  orderCode: string;
  consignmentCode: string;
  hasRejectedProducts: boolean;
  retryCount: number;
  metadata: MetaData;
  products: Product[];
  reason: Reason;
  posInformation: PosInformation;
  deliveryInformation: DeliveryInformation;
  pickUpInformation: PickUpInformation;
  paymentDetail: PaymentDetail;
  statuses: Statuses[];
}

export interface PlanningOrdersDataResponse {
  pageNumber: number;
  totalElements: number;
  totalPages: number;
  continuationToken?: string;
  elements: Order[];
}

export interface PlanningRoutesDataResponse {
  pageNumber: number;
  totalElements: number;
  totalPages: number;
  continuationToken?: string;
  elements: Route[];
}

export interface StandardProducts {
  products: ProductsDataTransform[];
}

interface Metadata {
  bundleType: string;
  bundleName?: string;
  campaignId?: string;
  reason?: {
    reasonCode: string;
    reasonDescription: string;
  };
}

interface Item {
  sku: string;
  name: Name;
  quantity: string;
  shippedQty: string;
  acceptedQty?: string;
  rejectedQty: string;
  unitPrice: string;
  imgUrl: string;
  uom: string;
  associatedCrates?: any;
}

interface BundleProduct {
  sku: string;
  campaignId: string;
  name: Name;
  quantity: string;
  shippedQty: string;
  rejectedQty: string;
  unitPrice: string;
  imgUrl: string;
  uom: string;
  orderedBarcodes?: any;
  associatedBarcodes?: string[];
  lmMetadata: Metadata;
  items?: Item[];
}

interface TransformedBundleProduct {
  campaignId: string;
  bundleName: string;
  bundleShippedQty: string;
  bundlePrice: string;
  //bundleCurrency: string;
  bundleRejectedQty: string;
  bundleProductQuantity: string;
  bundleConstituents: {
    bundleProductName: string;
    bundleProductQuantity: string;
    bundleProductRejectedQty: string;
    bundleProductShippedQty: string;
  }[];
  reason?: {
    reasonCode: string;
    reasonDescription: string;
  };
}

interface TransformedBundleProductForRejection
  extends TransformedBundleProduct {
  id: string;
  rejectedQty: string;
  rejectedQtyList: { name: string; value: string }[];
  selectedRejectedQty: { name: string; value: string }[];
  itemDeliveryReason: { name: string; value: string }[];
  showReason: boolean;
  error: string;
}

export const lastMilePlanningOrdersDataTransform = (
  response: PlanningOrdersDataResponse
): any[] => {
  const transformedArray: any[] = [];
  response.elements.forEach((order: Order) => {
    const {
      id,
      metadata,
      posInformation: { store, storeName },
      deliveryInformation,
      paymentDetail,
      orderCode,
      products,
      reason,
      consignmentCode,
      statuses,
    } = order;
    // TODO: map/add actual objects too for drilling down content or store in state
    const deliveryDate = moment(
      deliveryInformation.deliverySlot.startTime,
      AppConstants.DATE_FORMAT_BACKEND
    );
    const startTime = new Date(deliveryInformation.deliverySlot.startTime);
    const endTime = new Date(deliveryInformation.deliverySlot.endTime);
    const customerContactInfo = deliveryInformation.customerContactInfo || {
      address: {},
    };
    const transactionDate = moment(
      metadata.transactionDate,
      AppConstants.DATE_FORMAT_BACKEND
    );
    const statusDate = metadata.lastStatusUpdatedTime
      ? moment(
          metadata.lastStatusUpdatedTime,
          AppConstants.DATE_FORMAT_BACKEND
        ).format(AppConstants.TIMESTAMP)
      : statuses && statuses.length
      ? moment(statuses[0].statusDate, AppConstants.DATE_FORMAT_BACKEND).format(
          AppConstants.TIMESTAMP
        )
      : "";
    const mpesaTimestamp = paymentDetail
      ? moment(
          paymentDetail.timestamp,
          AppConstants.DATE_FORMAT_BACKEND
        ).format(AppConstants.TIMESTAMP)
      : "";
    transformedArray.push({
      id: id,
      hubCode: store,
      hubName: storeName,
      deliveryArea: customerContactInfo.address
        ? customerContactInfo.address.area
        : "",
      consignmentId: consignmentCode,
      type: metadata.category,
      deliveryDate: deliveryDate.format(AppConstants.DATE_FORMAT),
      startEndTime: `${localTimeString(startTime)} - ${localTimeString(
        endTime
      )}`,
      deliveryStatus: metadata.deliveryStatus,
      statusDate: statusDate,
      products: products && products.length ? products.length : 0,
      numberOfItems:
        metadata.numberOfItems && metadata.numberOfItems > 0
          ? metadata.numberOfItems
          : products && products.length
          ? products.length
          : 0,
      crates: deliveryInformation.totalCratesCount,
      orderId: orderCode,
      transactionDate: transactionDate.format(AppConstants.DATE_FORMAT),
      customerName: customerContactInfo.name,
      customerEmail: customerContactInfo.email,
      customerMobileNumber: customerContactInfo.phone,
      deliveryStreetAddress: getAddressInfo(customerContactInfo.address),
      landmark: customerContactInfo.address.landmark,
      paymentType: metadata.paymentType,
      totalAmount: fixDecimals(
        2,
        metadata.totalAmount ? metadata.totalAmount.toString() : ""
      ),
      codAmount: fixDecimals(
        2,
        metadata.amountToCollect ? metadata.amountToCollect.toString() : ""
      ),
      collectedAmount: metadata.collectedAmount
        ? parseFloat(metadata.collectedAmount.toString()).toFixed(2)
        : "0.00",
      carrier: deliveryInformation.carrier,
      deliveryProposition: metadata.deliveryType,
      routeId: metadata.routeName,
      routeStatus: metadata.routeStatus,
      driver: deliveryInformation.driver ? deliveryInformation.driver.name : "",
      deliveryZone:
        metadata && metadata?.deliveryZone ? metadata?.deliveryZone : "",
      vendor: deliveryInformation.driver
        ? deliveryInformation.driver?.vendor
        : "",
      helper: deliveryInformation.helper ? deliveryInformation.helper.name : "",
      vehicle: deliveryInformation.vehicle
        ? deliveryInformation.vehicle.name
        : "",
      plannedDuration: deliveryInformation.dynamicTransitTime
        ? deliveryInformation.dynamicTransitTime
        : deliveryInformation.estimatedTransitTime,
      actualDuration: deliveryInformation.actualTransitTime
        ? deliveryInformation.actualTransitTime
        : "",
      deliveryFailedReason: deliveryInformation?.reason
        ? deliveryInformation?.reason?.reasonDescription
        : "",
      paybill: paymentDetail ? paymentDetail.payBillNo : "",
      mpesaphoneNumber: paymentDetail ? paymentDetail.paymentPhoneNumber : "",
      refNo: paymentDetail ? paymentDetail.uniqueReferenceNumber : "",
      address:
        customerContactInfo && customerContactInfo.address
          ? getAddressInfo(customerContactInfo.address)
          : "-",
      requestedAmt: paymentDetail ? paymentDetail.collectedAmount : "",
      mpesaAttemptNo: paymentDetail
        ? paymentDetail.transactionInitiatedTries
        : "",
      mpesaStatus: paymentDetail ? paymentDetail.transactionStatus : "",
      timestamp: paymentDetail ? mpesaTimestamp : "",
      addressStatus:
        customerContactInfo && customerContactInfo?.address?.status
          ? customerContactInfo.address.status
          : "",
      rescheduled: deliveryInformation?.deliverySlot?.rescheduled || false,
      deliveryExecutionStatus: deliveryInformation?.deliveryExecutionStatus,
    });
  });
  return [...transformedArray];
};

export const lastMilePlanningRoutesDataTransform = (
  response: PlanningRoutesDataResponse
): any[] => {
  const transformedArray: any[] = [];
  response.elements.forEach((order: Route) => {
    const {
      id,
      routeId,
      hubCode,
      hubName,
      carrier,
      deliveryProposition,
      transitTime,
      deliveryArea,
      deliverySlot,
      plannedDate,
      status,
      totalConsignment,
      assignedVehicle,
      assignee,
      helper,
      createdBy,
      totalCrates,
      actualTransitTime,
      parentRouteId,
      routeType = "",
      actualTransitDistance,
    } = order;
    // TODO: map/add actual objects too for drilling down content or store in state
    const deliveryDate = moment(plannedDate, AppConstants.DATE_FORMAT_BACKEND);
    transformedArray.push({
      id: id,
      hubCode: hubCode,
      hubName: hubName,
      deliveryArea: deliveryArea && deliveryArea.length ? deliveryArea : [],
      deliveryDate: deliveryDate.format(AppConstants.DATE_FORMAT),
      deliverySlot: deliverySlot && deliverySlot.length ? deliverySlot : [],
      consignments: totalConsignment,
      crates: totalCrates,
      carrier: carrier,
      deliveryProposition: deliveryProposition,
      routeId: routeId,
      routeStatus: status,
      driver: assignee ? assignee.name : "",
      vehicle: assignedVehicle ? assignedVehicle.name : "",
      helper: helper ? helper.name : "",
      createdBy: createdBy,
      plannedDuration: transitTime,
      actualDuration: actualTransitTime,
      actions: order,
      parentRouteId: parentRouteId,
      routeType: routeType,
      actualDistance: actualTransitDistance?.formattedDistance || "",
    });
  });
  return [...transformedArray];
};

export const getConsignmentStatusField = (
  fieldName: string,
  statusCode: string
) => {
  const statuses: any =
    AppConstants.PLANNING_CONSTANTS.CONSIGNMENTS_DATA.STATUSES;
  return statusCode && statuses[statusCode]
    ? statuses[statusCode][fieldName]
    : "";
};

export const getRouteStatusField = (fieldName: string, statusCode: string) => {
  const statuses: any = AppConstants.PLANNING_CONSTANTS.ROUTES_DATA.STATUSES;
  return statusCode && statuses[statusCode]
    ? statuses[statusCode][fieldName]
    : "";
};

export const bundleRejectionDataTransform = (
  response: TransformedBundleProduct[]
) => {
  const transformedArray: TransformedBundleProductForRejection[] = [];
  response &&
    response.forEach((product: any) => {
      const { campaignId, bundleRejectedQty, bundleShippedQty } = product;

      let showReason = false;
      const rejectedItems = bundleRejectedQty || 0;
      const initialQty = rejectedItems;

      const shipedQty = bundleShippedQty;
      let newObj: any = [];
      let selectRejected = [];
      for (let i = 0; i <= shipedQty; i++) {
        newObj = transformIntoObjArrays(i, shipedQty);
      }
      selectRejected = transformIntoObj(initialQty);
      const deliveryReason = [];
      if (product.reason) {
        deliveryReason.push({
          name: product.reason.reasonDescription,
          value: product.reason.reasonCode,
        });
      }
      //Logic to showReason only when the rejectedQty is not zero
      if (
        bundleRejectedQty !== "0" &&
        !isUndefined(bundleRejectedQty) &&
        !bundleRejectedQty.includes("-")
      ) {
        showReason = true;
      }
      transformedArray.push({
        ...product,
        id: campaignId,
        rejectedQty: rejectedItems,
        rejectedQtyList: newObj,
        selectedRejectedQty: selectRejected,
        itemDeliveryReason: deliveryReason,
        showReason: showReason,
        error: "",
      });
    });
  return [...transformedArray];
};

export const createFilterPayload = (
  payload: any,
  data?: any,
  isLoadMore?: boolean,
  isExpress?: any,
  isRoute?: boolean
) => {
  payload.attributes = [];
  if (payload.hub) {
    if (payload.hub.value) {
      let hubValue = payload.hub.value.map((item: any) => item.value);
      let hubNameValue = [];
      if (payload.hubName && payload.hubName.value.length) {
        hubNameValue = payload.hubName.value.map((item: any) => item.value);
      }
      hubValue = hubValue.concat(hubNameValue);
      const selectAllIdx = hubValue.indexOf(AppConstants.SELECT_ALL.value);
      const selectNoneIdx = hubValue.indexOf(AppConstants.SELECT_NONE.value);
      if (selectAllIdx > -1) {
        hubValue = [];
      }
      if (selectNoneIdx > -1) {
        hubValue = [];
      }
      payload.attributes.push({
        key: payload.hub.fieldName,
        value: getUniqueValues(hubValue),
      });
      delete payload.hub;
      delete payload.hubName;
    } else {
      payload.attributes.push({
        key: payload.hub.fieldName,
        value: [],
      });
      delete payload.hub;
      delete payload.hubName;
    }
  }
  if (payload.deliveryArea) {
    let attrValue = [];
    if (payload.deliveryArea.value) {
      attrValue = getFilterAttributeValue(payload.deliveryArea.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.deliveryArea.fieldName,
      value: attrValue,
    });
    delete payload.deliveryArea;
  }
  if (payload.addressStatus) {
    let attrValue = [];
    if (payload.addressStatus.value) {
      attrValue = getFilterAttributeValue(payload.addressStatus.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.addressStatus.fieldName,
      value: attrValue,
    });
    delete payload.addressStatus;
  }
  if (payload.deliveryZone) {
    let attrValue = [];
    if (payload.deliveryZone.value) {
      attrValue = getFilterAttributeValue(payload.deliveryZone.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.deliveryZone.fieldName,
      value: attrValue,
    });
    delete payload.deliveryZone;
  }
  if (payload.vendor) {
    let attrValue = [];
    if (payload.vendor.value) {
      attrValue = getFilterAttributeValue(payload.vendor.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.vendor.fieldName,
      value: attrValue,
    });
    delete payload.vendor;
  }

  if (payload.orderType) {
    let attrValue = [];
    if (payload.orderType.value) {
      attrValue = getFilterAttributeValue(payload.orderType.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.orderType.fieldName,
      value: attrValue,
    });
    delete payload.orderType;
  }
  if (payload.deliveryStatus) {
    let attrValue = [];
    if (payload.deliveryStatus.value) {
      attrValue = getFilterAttributeValue(payload.deliveryStatus.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.deliveryStatus.fieldName,
      value: attrValue,
    });
    delete payload.deliveryStatus;
  }
  if (payload.paymentType) {
    let attrValue = [];
    if (payload.paymentType.value) {
      attrValue = getFilterAttributeValue(payload.paymentType.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.paymentType.fieldName,
      value: attrValue,
    });
    delete payload.paymentType;
  }
  if (payload.carrier) {
    let attrValue = [];
    if (payload.carrier.value) {
      attrValue = getFilterAttributeValue(payload.carrier.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.carrier.fieldName,
      value: attrValue,
    });
    delete payload.carrier;
  }
  if (isExpress) {
    if (payload.deliveryProposition) {
      payload.attributes.push({
        key: payload.deliveryProposition.fieldName,
        value: ["EXP"],
      });
      delete payload.deliveryProposition;
    }
  } else {
    if (payload.deliveryProposition) {
      /* let attrValue = [];
      if (payload.deliveryProposition.value) {
        attrValue = getFilterAttributeValue(payload.deliveryProposition.value);
      } else {
        attrValue = [];
      } */
      payload.attributes.push({
        key: payload.deliveryProposition.fieldName,
        value: ["STD"],
      });
      delete payload.deliveryProposition;
    }
  }
  if (payload.routeId) {
    let attrValue = [];
    if (payload.routeId.value) {
      attrValue = getFilterAttributeValue(payload.routeId.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.routeId.fieldName,
      value: attrValue,
    });
    delete payload.routeId;
  }
  if (payload.routeStatus) {
    let attrValue = [];
    if (payload.routeStatus.value) {
      attrValue = getFilterAttributeValue(payload.routeStatus.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.routeStatus.fieldName,
      value: attrValue,
    });
    delete payload.routeStatus;
  }
  if (payload.driver) {
    let attrValue = [];
    if (payload.driver.value) {
      attrValue = getFilterAttributeValue(payload.driver.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.driver.fieldName,
      value: attrValue,
    });
    delete payload.driver;
  }
  if (payload.vehicle) {
    let attrValue = [];
    if (payload.vehicle.value) {
      attrValue = getFilterAttributeValue(payload.vehicle.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.vehicle.fieldName,
      value: attrValue,
    });
    delete payload.vehicle;
  }
  if (payload.helper) {
    let attrValue = [];
    if (payload.helper.value) {
      attrValue = getFilterAttributeValue(payload.helper.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.helper.fieldName,
      value: attrValue,
    });
    delete payload.helper;
  }
  if (payload.deliveryFailedReason) {
    let attrValue = [];
    if (payload.deliveryFailedReason.value) {
      attrValue = getFilterAttributeValue(payload.deliveryFailedReason.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.deliveryFailedReason.fieldName,
      value: attrValue,
    });
    delete payload.deliveryFailedReason;
  }
  if (payload.createdBy) {
    let attrValue = [];
    if (payload.createdBy.value) {
      attrValue = getFilterAttributeValue(payload.createdBy.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.createdBy.fieldName,
      value: attrValue,
    });
    delete payload.createdBy;
  }
  if (payload.displayRouteStatus) {
    payload.attributes.push({
      key: payload.displayRouteStatus.fieldName,
      value: payload.displayRouteStatus.value || [],
    });
    delete payload.displayRouteStatus;
  }
  if (payload.deliverySlot && payload.deliverySlot.value) {
    let delSlotValue = payload.deliverySlot.value.map(
      (item: any) => item.value
    );
    let dateRange = [];
    const selectAllIdx = delSlotValue.indexOf(AppConstants.SELECT_ALL.value);
    if (payload.dateRange && payload.dateRange.value) {
      const allDates = enumerateDaysBetweenDates(
        payload.dateRange.value.startDate,
        payload.dateRange.value.endDate
      );
      if (selectAllIdx > -1 || !delSlotValue.length) {
        payload.isDateRange = true;
        dateRange.push({
          startTime: moment(
            payload.dateRange.value.startDate,
            AppConstants.DATE_FORMAT
          )
            .hours(0)
            .minutes(0)
            .seconds(0)
            .format(AppConstants.DATE_FORMAT_BACKEND),
          endTime: moment(
            payload.dateRange.value.endDate,
            AppConstants.DATE_FORMAT
          )
            .hours(23)
            .minutes(59)
            .seconds(59)
            .format(AppConstants.DATE_FORMAT_BACKEND),
        });
      } else {
        payload.isDateRange = false;
        delSlotValue.forEach((slot: any) => {
          const splitSlot = slot.split("-");
          const startSlot = splitSlot[0].split(":");
          const endSlot = splitSlot[1].split(":");
          if (allDates.length) {
            allDates.forEach((date: string) => {
              dateRange.push({
                startTime: moment(date, AppConstants.DATE_FORMAT)
                  .hours(startSlot[0])
                  .minutes(startSlot[1])
                  .seconds(startSlot[2])
                  .format(AppConstants.DATE_FORMAT_BACKEND),
                endTime: moment(date, AppConstants.DATE_FORMAT)
                  .hours(endSlot[0])
                  .minutes(endSlot[1])
                  .seconds(endSlot[2])
                  .format(AppConstants.DATE_FORMAT_BACKEND),
              });
            });
          }
        });
      }
      if (payload.reset) {
        delete payload.isDateRange;
      }
    }
    payload.dateRange = dateRange;
    delete payload.displayRouteStatus;
    delete payload.deliverySlot;
    delete payload.reset;
  }
  if (payload.deliveryExecutionStatus) {
    let attrValue = [];
    if (payload.deliveryExecutionStatus.value) {
      attrValue = getFilterAttributeValue(
        payload.deliveryExecutionStatus.value
      );
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.deliveryExecutionStatus.fieldName,
      value: attrValue,
    });
    delete payload.deliveryExecutionStatus;
  }
  if (isLoadMore && data) {
    payload.pageNumber =
      data.pageNumber + 1 < data.totalPages
        ? data.pageNumber + 1
        : payload.pageNumber;
  }
  if (data.continuationToken && payload.pageNumber) {
    payload.requestContinuationToken = data.continuationToken;
  }
  if (isExpress) {
    payload.isExpress = true;
  }
  return payload;
};

export const createLogsDownloadPayload = (payload: any, consignment?: string) => {
  // Extract date range
  let dateRange;
  if (payload.dateRange && payload.dateRange.value) {
    dateRange = {
      startTime: moment(
        payload.dateRange.value.startDate,
        AppConstants.DATE_FORMAT
      )
        .hours(0)
        .minutes(0)
        .seconds(0)
        .format(AppConstants.DATE_FORMAT_BACKEND),
      endTime: moment(
        payload.dateRange.value.endDate,
        AppConstants.DATE_FORMAT
      )
        .hours(23)
        .minutes(59)
        .seconds(59)
        .format(AppConstants.DATE_FORMAT_BACKEND),
      "rescheduled": false
    };
  }

  let searchAttribute;
  if (consignment) {
    searchAttribute = {
      key: 'consignmentCode',
      value: consignment,
      "isExactMatch": false
    };
  }

  return {
    pageNumber: payload.pageNumber ?? 0,
    pageSize: payload.pageSize ?? 20,
    dateRange,
    searchAttribute,
    isDateRange: true,
    isSecureExport: false,
  };
};

export const createPayloadForExcelExport = (
  payload: any,
  data?: any,
  isLoadMore?: boolean,
  isExpress?: boolean,
  isRoute?: boolean
) => {
  const newPayload = createFilterPayload(
    payload,
    data,
    isLoadMore,
    isExpress,
    isRoute
  );
  const orderFilterRequestDto = { orderFilterRequestDto: newPayload };
  return orderFilterRequestDto;
};

export const createRoutesPayloadForExcelExport = (
  payload: any,
  data?: any,
  isLoadMore?: boolean,
  isExpress?: boolean,
  isRoute?: boolean
) => {
  const newPayload = createFilterPayload(
    payload,
    data,
    isLoadMore,
    isExpress,
    isRoute
  );
  const routeFilterRequestDto = { routeFilterRequestDto: newPayload };
  return routeFilterRequestDto;
};

export const createCratesInfoPayload = (payload: any) => {
  if (payload) {
    let consignmentCode;
    let storeCode;
    if (payload.consignmentCode) {
      consignmentCode = payload.consignmentCode;
    }
    if (payload.posInformation) {
      storeCode = payload.posInformation.store;
    }
    const consignmentCodePosCodeMap = {
      consignmentCodePosCodeMap: {
        [consignmentCode]: storeCode,
      },
    };
    return consignmentCodePosCodeMap;
  }
};

export const getDiscardRoutePayload = (params: any) => {
  const routeId = sessionStorage.getItem("routeId");
  const consignments = JSON.parse(
    sessionStorage.getItem("extraConsignments") || "[]"
  );
  const previousConsignments = params.previousCons;
  const responsePayload: any = {
    countryCode: sessionStorage.getItem(AppConstants.COUNTRY_CODE),
    routeId: routeId || "",
    previousConsignments: previousConsignments,
  };
  if (consignments.length) {
    responsePayload.consignmentsAdded = consignments;
  }
  return responsePayload;
};

export const createPublishRoutesPayload = (routes: RowData[]) => {
  return routes.length
    ? {
        countryCode: sessionStorage.getItem(AppConstants.COUNTRY_CODE),
        ids: routes.map((route: RowData) => route.id),
      }
    : null;
};

export const createConsignmentReplanningPayload = (params: any) => {
  const payload: any = {
    consignmentId: params.id,
  };
  if (params.status === DeliveryConstants.CONSIGNMENTS_DATA.DEFAULT_REPLANNING_SELECTION) {
    payload.reScheduled = true;
    const splitSlot = params.slot.split("-");
    const startSlot = splitSlot[0].split(":");
    const endSlot = splitSlot[1].split(":");
    payload.timeSlot = {
      startTime: moment(params.date.startDate, AppConstants.DATE_FORMAT_UI).hours(startSlot[0]).minutes(startSlot[1]).seconds(startSlot[2]).format(AppConstants.DATE_FORMAT_BACKEND),
      endTime: moment(params.date.endDate, AppConstants.DATE_FORMAT_UI).hours(endSlot[0]).minutes(endSlot[1]).seconds(endSlot[2]).format(AppConstants.DATE_FORMAT_BACKEND),
    };
  } else {
    payload.reScheduled = false;
    payload.status = params.selection;
    payload.comments = params.commentsValue;
    payload.reason = params.reason
      ? {
          reasonCode: params.reason.reasonCode,
          reasonDescription: params.reason.reasonDescription,
          deliveryStatus: params.reason.deliveryStatus,
          comment: params.commentsValue,
        }
      : {};
      if (params.driverId) {
        payload.driverId = params.driverId;
      }
      if (params.helperId) {
        payload.helperId = params.helperId;
      }  
  }
  return payload;
}

export const createPayloadForCalculatingLineItems = (
  params: any,
  consignment: any
) => {
  const payload: any = {};
  const lineItems: any = {};
  if (params.length > 0) {
    params.forEach((item: any) => {
      lineItems[item.entryNumber] = {
        rejectedQty: item.rejectedQty,
      };
    });
  }
  payload.consignmentCode = consignment.consignmentCode;
  payload.rejectedLineItems = lineItems;
  return payload;
};

export const createPayloadForUpdatingLineItems = (
  params: any,
  consignment: any
) => {
  const payload: any = {};
  const prodLineItems: any = {};
  if (params.length > 0) {
    params.forEach((item: any) => {
      prodLineItems[item.entryNumber] = {
        entryNumber: item.entryNumber,
        rejectedQty: item.rejectedQty,
        sku:
          item.substitutedProducts?.length === 1
            ? item.substitutedProducts[0]?.sku
            : item.id,
        reason: {
          reasonCode:
            item.itemDeliveryReason?.length === 1
              ? item.itemDeliveryReason[0].value
              : "",
          reasonDescription:
            item.itemDeliveryReason?.length === 1
              ? item.itemDeliveryReason[0].name
              : "",
        },
      };
    });
  }
  payload.consignmentCode = consignment.consignmentCode;
  payload.lineItems = prodLineItems;
  return payload;
};

export const createPayloadGetAllSlots = (pos: string, deliveryZone: string) => {
  return {
    pos: pos,
    zone: deliveryZone,
  };
};

export const createPayloadForReplanDuringPartialRejection = (
  params: any,
  consignment: any,
  driverId?: string,
  helperId?: string,
  reason?: any,
) => {
  const payload: any = {};
  const prodLineItems: any = {};
  const lineItems: any = {};
  if (params.length > 0) {
    params.forEach((item: any) => {
      lineItems[item.entryNumber] = {
        rejectedQty: item.rejectedQty,
      };
    });
  }
  if (params.length > 0) {
    params.forEach((item: any) => {
      prodLineItems[item.entryNumber] = {
        entryNumber: item.entryNumber,
        rejectedQty: item.rejectedQty,
        sku:
          item.substitutedProducts?.length === 1
            ? item.substitutedProducts[0]?.sku
            : item.id,
        reason: {
          reasonCode:
            item.itemDeliveryReason?.length === 1
              ? item.itemDeliveryReason[0].value
              : "",
          reasonDescription:
            item.itemDeliveryReason?.length === 1
              ? item.itemDeliveryReason[0].name
              : "",
        },
      };
    });
  }
  if (driverId) {
    payload.driverId = driverId;
  }
  if (helperId) {
    payload.helperId = helperId;
  }  
  payload.consignmentCode = consignment.consignmentCode;
  payload.lineItems = prodLineItems;
  payload.rejectedLineItems = lineItems;
  payload.reScheduled = false;
  payload.status = DeliveryConstants.CONSIGNMENTS_DATA.DELIVERED;
  payload.comments = "";
  payload.reason = reason
    ? {
        reasonCode: reason.reasonCode,
        reasonDescription: reason.reasonDescription,
        deliveryStatus: reason.deliveryStatus,
        comment: reason.comment,
      }
    : {};
  return payload;
};

export const createPayloadBundlePartialRejection = (
  params: TransformedBundleProductForRejection[]
) => {
  const payload: any = {};
  const prodLineItems: any = {};
  const bundleLineItems: any = {};
  if (params?.length > 0) {
    params.forEach((item: any) => {
      if (item?.rejectedQty > 0) {
        bundleLineItems[item.id] = {
          rejectedQty: item.rejectedQty,
        };
      }
    });
  }
  if (params?.length > 0) {
    params.forEach((item: any) => {
      if (item?.rejectedQty > 0) {
        prodLineItems[item.id] = {
          campaignId: item.id,
          rejectedQty: item.rejectedQty,
          reason: {
            reasonCode:
              item.itemDeliveryReason?.length === 1
                ? item.itemDeliveryReason[0].value
                : "",
            reasonDescription:
              item.itemDeliveryReason?.length === 1
                ? item.itemDeliveryReason[0].name
                : "",
          },
        };
      }
    });
  }
  payload.bundleLineItems = prodLineItems;
  payload.rejectedBundleLineItems = bundleLineItems;
  return payload;
};

export const createDeleteRoutePayload = (id: string) => {
  return id
    ? {
        countryCode: sessionStorage.getItem(AppConstants.COUNTRY_CODE),
        id: id,
      }
    : null;
};

export const createBulkDeleteRoutePayload = (routes: Array<any>) => {
  return routes.length
    ? {
        countryCode: sessionStorage.getItem(AppConstants.COUNTRY_CODE),
        routeIds: routes.map(({ id }) => id),
      }
    : null;
};

export const createGetAllPlannedRoutesPayload = (params: any) => {
  const basePayload = getTimeSlotPayload(params);
  if (params.isExpress) {
    return {
      ...basePayload,
      isExpress: params.isExpress,
    };
  } else {
    return basePayload
  }
};

export const createUpdateOrderStatusPayload = (params: any) => {
  return {
    countryCode: sessionStorage.getItem(AppConstants.COUNTRY_CODE),
    consignmentIds: params.consignments,
    displayRouteStatus: params.status,
    routeId: params.routeId,
  };
};

export const getPlannedRoutesData = (routeData: any) => {
  const plannedSlots = [];
  if (routeData.PLANNED) {
    const plannedValue: any = routeData.PLANNED;
    for (const slotKey in plannedValue) {
      const slotValue: any = plannedValue[slotKey];
      const slot: any = {
        code: slotKey,
        value: slotValue.slotValue,
        data: [],
      };
      let totalConsignments = 0;
      let totalCrates = 0;
      const elements: any = slotValue.elements.map((route: any) => {
        totalConsignments += route.totalConsignment;
        totalCrates += route.totalCrates;
        return {
          code: route.id,
          value: route.routeId,
          data: route,
          slot: slotKey,
          slotValue: slotValue.slotValue,
        };
      });
      slot.data = elements;
      slot.totalConsignments = totalConsignments;
      slot.totalCrates = totalCrates;
      plannedSlots.push(slot);
    }
  }
  return plannedSlots;
};


export const transformRouteConsignments = (consignments: any[]) => {
  const routeConsignments: any = {};
  const seqConsignments: any = [];
  consignments.forEach((consignment: any) => {
    if (consignment.id) {
      const newConsignment: any = {
        code: consignment.id,
        value: consignment.id,
        data: consignment,
        area:
          consignment.deliveryInformation &&
          consignment.deliveryInformation.customerContactInfo &&
          consignment.deliveryInformation.customerContactInfo.address.area,
        slot:
          consignment.deliveryInformation &&
          consignment.deliveryInformation.deliverySlot.deliverySlotTime,
      };
      seqConsignments.push(consignment.id);
      if (routeConsignments[newConsignment.area]) {
        if (routeConsignments[newConsignment.area][newConsignment.slot]) {
          routeConsignments[newConsignment.area][newConsignment.slot].push(
            newConsignment
          );
        } else {
          routeConsignments[newConsignment.area][newConsignment.slot] = [
            newConsignment,
          ];
        }
      } else {
        routeConsignments[newConsignment.area] = {
          [newConsignment.slot]: [newConsignment],
        };
      }
    }
  });
  return {
    routeConsignments: routeConsignments,
    seqConsignments: seqConsignments,
  };
};

export const getAllAreasAndSlots = (areas: any[]) => {
  const allAreas: string[] = [];
  areas.forEach((area: any) => {
    const slots = area.data;
    slots.forEach((slot: any) => {
      allAreas.push(`${area.code}-${slot.code}`);
    });
    allAreas.push(area.code);
  });
  return allAreas;
};

export const checkTerminalStatus = (deliveryStatus: string) => {
  return (
    AppConstants.PLANNING_CONSTANTS.CONSIGNMENTS_DATA.TERMINAL_STATUSES.indexOf(
      deliveryStatus
    ) > -1
  );
};

export const createPayloadToReleaseAssignee = (params: any) => {
  const payload: any = {};
  if (params) {
    payload.routeId = params;
  }
  return payload;
};

export const createPayloadToMarkAddressUnverified = (
  consignment: any,
  address: any
) => {
  const payload = {
    consignmentCode: consignment.consignmentCode,
    address: {
      status: ADDRESS_STATUS.UNVERIFIED,
    },
  };
  return payload;
};

export const transformBundleData = (
  bundleProducts: BundleProduct[]
): TransformedBundleProduct[] => {
  const transformedData: TransformedBundleProduct[] = [];

  bundleProducts.forEach((bundle: any) => {
    const unitBundle =
      bundle?.uom === "Grams" ? displayUnitGrams(bundle.uom) : "";
    const bundleObj: TransformedBundleProduct = {
      campaignId: bundle?.campaignId,
      bundleName: decodeHtmlEntities(bundle?.name?.en),
      bundleShippedQty: bundle?.shippedQty,
      bundlePrice: bundle?.unitPrice,
      bundleRejectedQty: bundle?.rejectedQty,
      bundleProductQuantity: bundle?.quantity,
      bundleConstituents: [],
      reason: bundle?.reason,
    };

    if (bundle.items) {
      bundle.items.forEach((item: any) => {
        const unit = item?.uom === "Grams" ? displayUnitGrams(item.uom) : "";
        const constituent = {
          bundleProductName: decodeHtmlEntities(item?.name?.en),
          bundleProductQuantity: `${item?.quantity} ${unit}`,
          bundleProductRejectedQty: item?.rejectedQty
            ? `${item?.rejectedQty} ${unit}`
            : "-",
          bundleProductShippedQty: `${item?.shippedQty || 0} ${unit}`,
        };
        bundleObj.bundleConstituents.push(constituent);
      });
    }

    transformedData.push(bundleObj);
  });

  return transformedData;
};
export const isRouteStatusOutForDelivery = (status: string | undefined) => {
  return (
    status ===
    AppConstants.PLANNING_CONSTANTS.CONSIGNMENTS_DATA.ORDER_STATUS
      .OUT_FOR_DELIVERY
  );
};

interface RouteObject {
  id: string;
  routeId: string;
  parentRouteId?: string;
  subRoutes?: RouteObject[];
  // other fields...
}

export const transformDataForSubRoutes = (response: any) => {
  const data = lastMilePlanningRoutesDataTransform(response);
  const result: RouteObject[] = [];
  const idToObjectMap = new Map(
    data.map((item: RouteObject) => [item.id, item])
  );
  data.forEach((item: Route) => {
    if (item.parentRouteId) {
      const parentObject = idToObjectMap.get(item.parentRouteId);
      if (parentObject) {
        if (!parentObject?.subRoutes) {
          parentObject.subRoutes = [];
        }
        parentObject.subRoutes.push(item);
      } else {
        result.push(item);
      }
    } else {
      result.push(item);
    }
  });
  return result;
};

interface NameObj {
  ar: string;
  en: string;
}
interface NameObj {
  ar: string;
  en: string;
}

export const transformedDataViewRoute = (response: any) => {
  return response?.map((route: any) => {
    return {
      routeId: route?.routeId,
      displayStatus: route?.displayStatus,
      orders: route.orders.map((order: any) => {
        return {
          consignmentCode: order?.consignmentCode,
          routeType: route?.routeType,
          slotValue: order?.slotValue,
          metadata: {
            deliveryStatus: order?.deliveryStatus,
            paymentType: order?.paymentType,
          },
          deliveryInformation: {
            totalCratesCount: order?.totalCratesCount,
            customerContactInfo: {
              address: order?.address?.fullAddress,
              phone: order?.customerPhone,
            },
          },
        };
      }),
    };
  });
};

export const getDeliveryExecutionStatusField = (
  fieldName: string,
  statusCode: string
) => {
  const statuses: any =
    AppConstants.PLANNING_CONSTANTS.CONSIGNMENTS_DATA.DELIVERY_EXECUTION_STATUS;
  if (statusCode === "ON-TIME") {
    statuses[statusCode] =
      AppConstants.PLANNING_CONSTANTS.CONSIGNMENTS_DATA.DELIVERY_EXECUTION_STATUS.ON_TIME;
  }
  return statusCode && statuses[statusCode]
    ? statuses[statusCode][fieldName]
    : "";
};
